import { defineStore } from 'pinia';
import { ref, computed, watch, nextTick } from 'vue';
import { useDebounceFn, useSessionStorage, whenever } from '@vueuse/core';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { getCvFile } from '../services/cvFile';
import * as logger from '../utils/logger';
// eslint-disable-next-line import/no-cycle
import { useProfile } from './profile-store';
// eslint-disable-next-line import/no-cycle
import { useAuth } from './auth-store';
// eslint-disable-next-line import/no-cycle
import { usePayloadStore } from './payload-store';
import { renderCv } from '../services/renderCv';
import { useErrorStore } from './error-store';
import { useJobDataStore } from './job-store';
import { track } from '../utils/tracking';

const CV_RENDER_DEBOUNCE_TIME = 300; // ms

function sortAttachments(a, b) {
    const older = new Date(a.createDate).valueOf() > new Date(b.createDate).valueOf();
    return older ? 1 : -1;
}

export const CV_TYPES = {
    NONE: 'none',
    CUSTOM: 'custom',
    KARRIEREAT: 'karriere',
};

export const useApplicationStore = defineStore('application', () => {
    const profile = useProfile();
    const auth = useAuth();

    /* State */
    const firstname = ref('');
    const lastname = ref('');
    const salutation = ref('k');
    const email = ref('');
    const countryCode = ref('+43');
    const nationalNumber = ref('');
    const applicationLetter = ref(null);
    const recaptchaResponse = ref(null);
    const attachments = ref([]);
    const uploadedRawCvFile = ref(null);
    const initialApplication = ref({
        firstname: '',
        lastname: '',
        attachmentsCount: 0,
        applicationLetter: null,
    });
    const selectedCvType = ref(CV_TYPES.NONE);
    const isKarriereAtCvSelected = computed(() => selectedCvType.value === CV_TYPES.KARRIEREAT);
    const isCustomCvSelected = computed(() => selectedCvType.value === CV_TYPES.CUSTOM);
    const isNoCvSelected = computed(() => selectedCvType.value === CV_TYPES.NONE);

    const selfUploadedCv = ref(null);
    const cvFile = ref({
        uuid: undefined,
        isCv: true,
        isDownloading: false,
        isRendering: false,
        name: '.pdf',
        size: 0,
        createDate: undefined,
        previewLink: undefined,
        progress: 25,
    });
    const hasSeenPreview = ref(false);
    const uuid = ref(null);
    const applicationUuid = ref(uuidv4());

    /* Extract CV */
    const cvExtractionUsed = ref(false);
    const extractedSuccess = ref(false);
    const extractedEmpty = ref(false);
    const extractedError = ref(false);
    const extractedFirstname = ref('');
    const extractedLastname = ref('');
    const extractedEmail = ref('');
    const extractedCountryCode = ref('+43');
    const extractedNationalNumber = ref('');

    /* Getters */
    const isDirty = computed(() => firstname.value !== initialApplication.value.firstname ||
            lastname.value !== initialApplication.value.lastname ||
            attachments.value.length !== initialApplication.value.attachmentsCount ||
            applicationLetter.value !== initialApplication.value.applicationLetter);
    const showApplicationLetter = computed(() => applicationLetter.value !== null);
    const activeAttachments = computed(() => cloneDeep(attachments.value)
        .filter(({ shouldBeAttachedToApplication }) => shouldBeAttachedToApplication).sort(sortAttachments));
    const hasActiveAttachments = computed(() => Boolean(activeAttachments.value.length));
    const anyProfileDocumentIsInActiveAttachments = computed(
        () => activeAttachments.value.some(attachment => attachment.isProfileDocument),
    );
    const anyUploadedDocumentIsInActiveAttachments = computed(
        () => activeAttachments.value.some(attachment => !attachment.isProfileDocument),
    );
    const phoneNumber = computed(() => {
        if (countryCode.value && nationalNumber.value) {
            return `${countryCode.value}${nationalNumber.value}`;
        }
        return '';
    });
    const extractedPhoneNumber = computed(() => {
        if (extractedCountryCode.value && extractedNationalNumber.value) {
            return `${extractedCountryCode.value}${extractedNationalNumber.value}`;
        }
        return '';
    });

    const lastRenderedCvUuid = useSessionStorage('lokarb:last-rendered-cv-uuid', null);
    const cvRelevantData = ref({
        firstname: firstname.value,
        surname: lastname.value,
        email: email.value,
        phoneNumber: phoneNumber.value,
    });

    // Use function over computed for better control on when to send a render call,
    // mostly to still allow eager model and therefore vuelidate validation while not sending too many render calls
    function updateCvRenderData() {
        cvRelevantData.value = {
            firstname: firstname.value,
            surname: lastname.value,
            email: email.value,
            phoneNumber: phoneNumber.value,
        };
    }

    const canRenderCvFromData = computed(() => {
        if (!firstname.value || !lastname.value || !email.value || !phoneNumber.value) return false;

        return true;
    });

    const inactiveAttachments = computed(
        () => cloneDeep(attachments.value).filter(
            ({ shouldBeAttachedToApplication }) => !shouldBeAttachedToApplication,
        ).sort(sortAttachments),
    );
    const isMailDifferentFromAccount = computed(() => auth.isLoggedIn && email.value !== auth.email);
    const isFirstnameDifferentFromAccount = computed(() => auth.isLoggedIn && firstname.value !== profile.firstname);
    const isLastnameDifferentFromAccount = computed(() => auth.isLoggedIn && lastname.value !== profile.lastname);
    const isSalutationDifferentFromAccount = computed(() => auth.isLoggedIn && salutation.value !== profile.salutation);
    const isPhoneNumberDifferentFromAccount = computed(
        () => auth.isLoggedIn &&
            (countryCode.value !== profile.countryCode ||
            nationalNumber.value !== profile.nationalNumber),
    );
    const selectedCv = computed(() => {
        if (selectedCvType.value === CV_TYPES.KARRIEREAT) return cvFile.value;
        if (selectedCvType.value === CV_TYPES.CUSTOM) return selfUploadedCv.value;
        return null;
    });
    const serializedAttachments = computed(() => {
        const serializedFiles = activeAttachments.value.map(({ uuid: attachmentId, isProfileDocument }) => ({
            type: 'attachment',
            origin: isProfileDocument ? 'profile' : 'upload',
            uuid: attachmentId,
        }));
        if (selectedCvType.value === CV_TYPES.KARRIEREAT) {
            serializedFiles.unshift({
                type: 'cv',
                origin: 'profile',
                uuid: cvFile.value.uuid,
            });
        } else if (selectedCvType.value === CV_TYPES.CUSTOM) {
            serializedFiles.unshift({
                type: 'cv',
                origin: 'upload',
                uuid: selfUploadedCv.value.uuid,
            });
        }
        return serializedFiles;
    });
    const isCvRendering = computed(() => cvFile.value.isRendering);

    const errorState = useErrorStore();

    const renderCvAction = useDebounceFn(async () => {
        const payload = usePayloadStore();

        errorState.cvErrors.clear();

        cvFile.value = {
            ...cvFile.value,
            isRendering: true,
        };

        const jobData = useJobDataStore();
        const requestBody = {
            jobAdId: jobData.jobId,
            jobAdTitle: jobData.title,
            atsUserUuid: payload.atsUserUuid,
            uuid: applicationUuid.value,
            ...cvRelevantData.value,
        };

        try {
            if (canRenderCvFromData.value) {
                const { data } = await renderCv(requestBody);

                lastRenderedCvUuid.value = data.id;

                cvFile.value = {
                    ...cvFile.value,
                    uuid: data.id,
                    isCv: true,
                    name: data.name,
                    size: data.size,
                    createDate: data.createDate,
                    previewLink: data.previewLink,
                    progress: undefined,
                };
            }
        } catch (e) {
            // prevent error from bubbling up
        } finally {
            cvFile.value.isRendering = false;
        }
    }, CV_RENDER_DEBOUNCE_TIME);

    async function fetchCvAction() {
        if (!profile.hasProfile) return;

        cvFile.value = {
            ...cvFile.value,
            isDownloading: true,
            progress: 25,
        };

        try {
            const { data } = await getCvFile(lastRenderedCvUuid.value);

            cvFile.value = {
                ...cvFile.value,
                uuid: data.id,
                isCv: true,
                name: data.name,
                size: data.size,
                createDate: data.createDate,
                previewLink: data.previewLink,
                progress: undefined,
            };
        } catch (exception) {
            logger.error({
                exception,
                message: 'Fetching cv file failed.',
            });
        } finally {
            cvFile.value.isDownloading = false;
        }
    }

    function addAttachment(attachment) {
        // eslint-disable-next-line no-param-reassign
        attachment.shouldBeAttachedToApplication = true;
        attachments.value.push(attachment);
    }

    function trackExtractedData() {
        if (extractedFirstname.value) {
            track({
                element: 'AP_F: basic information extracted',
                elementDetail: 'first name',
            });
        }
        if (extractedLastname.value) {
            track({
                element: 'AP_F: basic information extracted',
                elementDetail: 'last name',
            });
        }
        if (extractedEmail.value) {
            track({
                element: 'AP_F: basic information extracted',
                elementDetail: 'email',
            });
        }
        if (extractedPhoneNumber.value) {
            track({
                element: 'AP_F: basic information extracted',
                elementDetail: 'phone',
            });
        }

        if ([extractedFirstname.value,
            extractedLastname.value,
            extractedEmail.value,
            extractedPhoneNumber.value].filter(Boolean).length < 3) {
            logger.info({
                message: 'CV Extraction rate is less than 3 out of 4',
                extra: {
                    applicationUuid: applicationUuid.value,
                },
            });
        }
    }

    function setExtractedData(data) {
        extractedFirstname.value = data.firstname || '';
        firstname.value = extractedFirstname.value;

        extractedLastname.value = data.surname || '';
        lastname.value = extractedLastname.value;

        extractedEmail.value = data.email || '';
        email.value = extractedEmail.value;

        extractedCountryCode.value = data.phoneNumber.countryCode;
        countryCode.value = extractedCountryCode.value;

        extractedNationalNumber.value = data.phoneNumber.nationalNumber || '';
        nationalNumber.value = extractedNationalNumber.value;

        trackExtractedData();

        return true;
    }

    function resetCvExtractionFields() {
        extractedFirstname.value = '';
        extractedLastname.value = '';
        extractedEmail.value = '';
        extractedCountryCode.value = '+43';
        extractedNationalNumber.value = '';
    }

    function set(profileData) {
        resetCvExtractionFields();
        email.value = profileData.basics.contact.email;

        if (profileData.flags.exists) {
            firstname.value = profileData.basics.firstname || '';
            lastname.value = profileData.basics.surname || '';
            salutation.value = profileData.basics.salutation ?? 'k';
            countryCode.value = profileData.basics.contact.mobile.countryCode;
            nationalNumber.value = profileData.basics.contact.mobile.nationalNumber || '';

            // Remove and re-attach all profile attachments to prevent no longer available files to stick around
            attachments.value = attachments.value.filter(attachment => !attachment.isProfileDocument);
            profileData.attachments.forEach((attachment) => {
                // eslint-disable-next-line no-param-reassign
                attachment.isProfileDocument = true;
                addAttachment(attachment);
            });
        }

        initialApplication.value = {
            firstname: firstname.value,
            lastname: lastname.value,
            attachmentsCount: attachments.value.length || 0,
            applicationLetter: applicationLetter.value,
        };

        updateCvRenderData();
    }

    function updateCheckedAttachment({ attachmentUuid, isChecked }) {
        const attachment = attachments.value.find(({ uuid: attachmentId }) => attachmentId === attachmentUuid);
        attachment.shouldBeAttachedToApplication = isChecked;
    }

    function activateAttachment(attachmentUuid) {
        updateCheckedAttachment({
            attachmentUuid,
            isChecked: true,
        });
    }

    function deactivateAttachment(attachmentUuid) {
        updateCheckedAttachment({
            attachmentUuid,
            isChecked: false,
        });
    }

    function removeApplicationLetter() {
        applicationLetter.value = null;
    }

    function addApplicationLetter() {
        applicationLetter.value = '';
    }

    function updateUuid(newUuid) {
        uuid.value = newUuid;
    }

    function saveUploadedRawCvFile(file) {
        uploadedRawCvFile.value = file;
    }

    let unwatchCvRender = () => {};
    let unwatchProfileFetch = () => {};
    function initializeCvRenderWatcherForUsersWithProfile() {
        unwatchProfileFetch();
        unwatchCvRender();

        unwatchProfileFetch = whenever(() => profile.profileFetched, async () => {
            if (!profile.hasProfile) return;

            unwatchProfileFetch();

            // wait for fields to autofill before starting watcher to avoid unnecessary render call
            await nextTick();

            unwatchCvRender = watch(cvRelevantData, (newValue, oldValue) => {
                if (!canRenderCvFromData.value) return;
                if (JSON.stringify(oldValue) === JSON.stringify(newValue)) return;

                renderCvAction();
            });
        }, { immediate: true });
    }
    initializeCvRenderWatcherForUsersWithProfile();

    function $reset() {
        initializeCvRenderWatcherForUsersWithProfile();

        firstname.value = '';
        lastname.value = '';
        salutation.value = 'k';
        email.value = '';
        phoneNumber.value = '';
        countryCode.value = '+43';
        applicationLetter.value = null;
        recaptchaResponse.value = null;
        attachments.value = [];
        initialApplication.value = {
            firstname: '',
            lastname: '',
            attachmentsCount: 0,
            applicationLetter: null,
        };
        selectedCvType.value = CV_TYPES.NONE;
        selfUploadedCv.value = null;
        cvFile.value = {
            isCv: true,
            isDownloading: false,
            isRendering: false,
            name: '.pdf',
            size: 0,
            createDate: undefined,
            previewLink: undefined,
            progress: 25,
        };
        hasSeenPreview.value = false;
        uuid.value = null;
        applicationUuid.value = uuidv4();
        cvExtractionUsed.value = false;
        extractedSuccess.value = false;
        extractedEmpty.value = false;
        extractedError.value = false;

        resetCvExtractionFields();
    }

    return {
        firstname,
        lastname,
        salutation,
        email,
        phoneNumber,
        countryCode,
        nationalNumber,
        applicationLetter,
        recaptchaResponse,
        attachments,
        initialApplication,
        selectedCvType,
        selfUploadedCv,
        uploadedRawCvFile,
        cvFile,
        hasSeenPreview,
        uuid,
        applicationUuid,

        cvExtractionUsed,
        extractedSuccess,
        extractedEmpty,
        extractedError,
        extractedFirstname,
        extractedLastname,
        extractedEmail,
        extractedCountryCode,
        extractedNationalNumber,
        extractedPhoneNumber,

        isDirty,
        showApplicationLetter,
        activeAttachments,
        hasActiveAttachments,
        anyProfileDocumentIsInActiveAttachments,
        anyUploadedDocumentIsInActiveAttachments,
        inactiveAttachments,
        isMailDifferentFromAccount,
        isFirstnameDifferentFromAccount,
        isLastnameDifferentFromAccount,
        isSalutationDifferentFromAccount,
        isPhoneNumberDifferentFromAccount,
        selectedCv,
        serializedAttachments,
        isCvRendering,
        isKarriereAtCvSelected,
        isCustomCvSelected,
        isNoCvSelected,

        updateCvRenderData,
        renderCv: renderCvAction,
        fetchCv: fetchCvAction,
        set,
        addAttachment,
        updateCheckedAttachment,
        activateAttachment,
        deactivateAttachment,
        removeApplicationLetter,
        addApplicationLetter,
        updateUuid,
        saveUploadedRawCvFile,
        setExtractedData,
        resetCvExtractionFields,
        $reset,
    };
}, {
    persist: {
        storage: sessionStorage,
    },
});
